import { createSlice } from '@reduxjs/toolkit';
import createFetchReducer from '@app/utils/createFetchReducer';

import {
	getSMSUserTemplateTypes,
	getUserTemplatesTypes,
	getWhatsappUserTemplateTypes,
	createTemplatesTypes,
	updateTemplatesTypes,
	updateTeamsOnUserTemplateTypes,
	deleteTemplatesTypes,
} from '@app/redux/userTemplates/types';
import { GenericEntityState, ResponseAction } from '@app/redux/types';
import { FIRST_PAGE } from '@app/redux/constants';
import { UserTemplateChannelsEnum } from '@app/redux/userTemplates/constants';
import { getUserTemplatesOrdered } from './utils';

const initialState: GenericEntityState = {
	loading: false,
	pagination: {
		pages: {},
		currentPage: null,
		totalPages: null,
	},
	error: {},
	response: {
		status: null,
		message: null,
	},
	userTemplates: [],
	smsTemplates: {
		pagination: {
			totalPages: null,
			currentPage: null,
			count: null,
			pages: {},
		},
		smsLoadedTemplatesCount: 0,
		templates: [],
	},
	waTemplates: {
		pagination: {
			totalPages: null,
			currentPage: null,
			count: null,
			pages: {},
		},
		waLoadedTemplatesCount: 0,
		templates: [],
	},
	custom: {
		createUserTemplateLoading: false,
		updateUserTemplateLoading: false,
		updateTeamsOnUserTemplateLoading: false,
		deleteUserTemplateLoading: false,
	},
};

export type userTemplatesType = typeof initialState;

function getUserTemplatesMapper(
	state: userTemplatesType,
	action: ResponseAction,
) {
	const page = action.response?.page;

	if (page === FIRST_PAGE) {
		state.userTemplates = action.response.userTemplates;
	} else {
		state.userTemplates = [
			...state.userTemplates,
			...action.response.userTemplates,
		];
	}

	state.pagination = {
		totalPages: action.response?.totalPages,
		currentPage: page,
		count: action?.response?.count,
		totalTabCount: action?.response?.totalTabCount,
		pages: {
			[page]: action.response.userTemplates,
		},
	};
}

function getSMSUserTemplateMapper(
	state: userTemplatesType,
	action: ResponseAction,
) {
	const page = action.response?.page;

	if (page === FIRST_PAGE) {
		state.smsTemplates.templates = action.response.userTemplates;
	} else {
		state.smsTemplates.templates = [
			...state.smsTemplates.templates,
			...action.response.userTemplates,
		];
	}

	state.smsTemplates.smsLoadedTemplatesCount =
		state.smsTemplates.templates.length || 0;
	state.smsTemplates.pagination = {
		totalPages: action.response?.totalPages,
		currentPage: page,
		count: action?.response?.count,
		pages: {
			...state.smsTemplates.pagination.pages,
			[page]: action.response.userTemplates,
		},
	};
}

function getWhatsAppUserTemplateMapper(
	state: userTemplatesType,
	action: ResponseAction,
) {
	const page = action.response?.page;

	if (page === FIRST_PAGE) {
		state.waTemplates.templates = action.response.userTemplates;
	} else {
		state.waTemplates.templates = [
			...state.waTemplates.templates,
			...action.response.userTemplates,
		];
	}

	state.waTemplates.waLoadedTemplatesCount =
		state.waTemplates.templates.length || 0;
	state.waTemplates.pagination = {
		totalPages: action.response?.totalPages,
		currentPage: page,
		count: action?.response?.count,
		pages: {
			...state.waTemplates.pagination.pages,
			[page]: action.response.userTemplates,
		},
	};
}

const createTemplatesMapper = (
	state: userTemplatesType,
	action: ResponseAction,
) => {
	const createdTemplate = action.response.userTemplate;
	const channel = createdTemplate?.channel;
	const insertInList = action.payload?.insertInList;

	if (channel === UserTemplateChannelsEnum.SMS) {
		if (insertInList) {
			state.smsTemplates.templates = getUserTemplatesOrdered([
				createdTemplate,
				...state.smsTemplates.templates,
			]);

			state.smsTemplates.pagination.count =
				state.smsTemplates.pagination.count + 1;
		}

		state.smsTemplates.smsLoadedTemplatesCount =
			state.smsTemplates.templates.length || 0;

		return;
	}

	if (channel === UserTemplateChannelsEnum.WHATSAPP) {
		if (insertInList) {
			state.waTemplates.templates = getUserTemplatesOrdered([
				createdTemplate,
				...state.waTemplates.templates,
			]);

			state.waTemplates.pagination.count =
				state.waTemplates.pagination.count + 1;
		}

		state.waTemplates.waLoadedTemplatesCount =
			state.waTemplates.templates.length || 0;
		return;
	}

	state.userTemplates = getUserTemplatesOrdered([
		createdTemplate,
		...state.userTemplates,
	]);
};

const updateTemplatesMapper = (
	state: userTemplatesType,
	action: ResponseAction,
) => {
	const updatedTemplate = action.response.userTemplate;
	const channel = updatedTemplate?.channel;

	if (channel === UserTemplateChannelsEnum.SMS) {
		state.smsTemplates.templates = getUserTemplatesOrdered(
			state.smsTemplates.templates,
			updatedTemplate,
		);
		return;
	}

	if (channel === UserTemplateChannelsEnum.WHATSAPP) {
		state.waTemplates.templates = getUserTemplatesOrdered(
			state.waTemplates.templates,
			updatedTemplate,
		);
		return;
	}

	state.userTemplates = getUserTemplatesOrdered(
		state.userTemplates,
		updatedTemplate,
	);
};

const userTemplateSlice = createSlice({
	name: 'userTemplate',
	initialState,
	reducers: {
		RESET_USER_TEMPLATE_LIST(state: userTemplatesType) {
			state.userTemplates = [];
			state.pagination = {
				pages: {},
				currentPage: null,
				totalPages: null,
			};

			state.smsTemplates = {
				pagination: {
					totalPages: null,
					currentPage: null,
					count: null,
					pages: {},
				},
				smsLoadedTemplatesCount: 0,
				templates: [],
			};

			state.waTemplates = {
				pagination: {
					totalPages: null,
					currentPage: null,
					count: null,
					pages: {},
				},
				waLoadedTemplatesCount: 0,
				templates: [],
			};

			state.custom = {
				createUserTemplateLoading: false,
				updateUserTemplateLoading: false,
				updateTeamsOnUserTemplateLoading: false,
				deleteUserTemplateLoading: false,
			};
		},
	},
	extraReducers: {
		...createFetchReducer(getUserTemplatesTypes, getUserTemplatesMapper),
		...createFetchReducer(getSMSUserTemplateTypes, getSMSUserTemplateMapper),
		...createFetchReducer(
			getWhatsappUserTemplateTypes,
			getWhatsAppUserTemplateMapper,
		),
		...createFetchReducer(createTemplatesTypes, createTemplatesMapper),
		...createFetchReducer(updateTemplatesTypes, updateTemplatesMapper),
		...createFetchReducer(deleteTemplatesTypes),
		...createFetchReducer(updateTeamsOnUserTemplateTypes),
	},
});

export default userTemplateSlice.reducer;
